import { useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, ZERO, addMoney, formatMoney, formatQuantity, hoy, newId, sumarDias, type Centavos, type Milli } from '@abastece/shared';
import type { Cliente, ListaPrecio, Pedido, Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { etiquetaCortaLista, etiquetaListaPrecio, listaPredeterminada, ordenarListas, precioDe, unidadesDe } from '../lib/catalogo.js';
import { costoConocido, enRango } from '../lib/costos.js';
import { factoresDe, hayEnLaTienda, noAlcanzaParaElPedido, pideMasQueAntes } from '../lib/apartados.js';
import { emitir, emitirYVer, probarEvento } from '../lib/emitir.js';
import { leerEnvio, textoDeEnvio } from '../lib/envio.js';
import {
  aLaLista,
  aLaPresentacion,
  agregarAlPedido,
  clienteLlamado,
  comoEsta,
  conCambios,
  conLista,
  lineasDelEvento,
  mismoPedido,
  sinPrecio,
  subtotalDe,
  totalDelPedido,
  type LineaPedido,
} from '../lib/pedidos.js';
import { enPractica } from '../lib/practica.js';
import { usePuede, useSesion } from '../lib/sesion.js';
import { alDiaConElServidor, confirmarConElServidor, suscribirSync, type SyncPhase } from '../sync/engine.js';
import { useT } from '../i18n/index.js';
import { etiquetaPresentacion, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { BuscadorProducto } from './BuscadorProducto.js';
import { Campo } from './Campo.js';
import { CampoEnvio } from './CampoEnvio.js';
import { EditorLinea, type LineaEditable } from './EditorLinea.js';
import { CLIENTE_VACIO, FormularioCliente, camposDe, saldoAnteriorDe, type BorradorCliente } from './FormularioCliente.js';
import { Ico } from './Iconos.js';
import { LineaDeCarrito, LineaEscaneada, type ListasDeLinea, type PresentacionesDeLinea } from './LineaDeCarrito.js';
import { Modal } from './Modal.js';
import { PedirPin } from './PedirPin.js';
import { Confirmar } from './Preguntar.js';
import { ElegirEscribiendo } from './ElegirEscribiendo.js';

const manana = () => sumarDias(hoy(), 1);

/**
 * Taking an order, rung up the way the till rings up a sale (the owner, 2026-10-04: the fields were
 * «shaped strangely», and scanning should «look more like the till»). Who it is for and when it is
 * delivered on top; the search under them, reading a USB scanner wherever the cursor is and with
 * the camera beside it; the till's own lines, picture, name, − count +, what it comes to and the bin
 * beside it; «Envío», the total and «Guardar pedido» at the foot, where the till has them and «Cobrar»
 * (the delivery since 2026-10-12, kept with the order and started from at the till). The same
 * product again, scanned or picked, is one more of it. A line's name opens the till's editor, for its
 * presentation and its price, under the till's rules (the owner, 2026-10-05): a price changed says
 * why, below the margin floor it takes the owner's PIN, and a driver sees it without changing it.
 * The sale that delivers the order records both, as if they had been typed at the till. And as at
 * the till, each line's price list is a row at the foot of its card (the owner, 2026-10-18: «set the
 * retail/wholesale price toggle on each item like in the sale screen»): the case of oil at wholesale,
 * the rest at the client's list. For whoever may charge another list without the owner, the owner and
 * a cashier (`cobrar_otra_lista`); the till rings each line up at its own.
 *
 * With `apartarPedidos` on (the owner, 2026-10-08: «we need stock reservation by both orders and the
 * till»), the order sets aside what it takes. A line past what is left once the pending orders have
 * theirs says so in red, and the order is not saved until it fits. And it is saved only with the
 * server's yes («Only with a connection»): the server puts every phone's orders in one order, so two
 * phones cannot both promise the last sack. No connection, no order; no answer in time, and the order
 * is cancelled behind itself, so that one reaching the server later holds nothing for anybody.
 *
 * An order still to deliver is changed on it too (the owner, 2026-10-15: «We need to be able to edit
 * orders in the order screen»): it opens as the order stands, every line at the price agreed, and
 * «Guardar cambios» says it as it now is (`pedido.actualizado`), once something has changed. Its list
 * stays the order's while its client does. What it held it keeps: only what it asks more of than
 * before is held to what is free, and only that needs the server's word. No answer in time, and the
 * order is put back as it was, behind the change.
 *
 * Self-contained: `routes/NuevoPedido.tsx` and `routes/EditarPedido.tsx` give it a screen of its own,
 * and anything else could.
 */
export function TomarPedido({
  clienteInicial,
  pedido,
  lineasIniciales,
  onGuardado,
}: {
  /** From «Pedido» beside a client in «Clientes»: the order already theirs. */
  clienteInicial?: string | undefined;
  /** An order still to deliver, as it stood when the screen opened: changed rather than taken. */
  pedido?: Pedido | undefined;
  /** Its lines as this screen keeps them (`lineasParaCambiar`). */
  lineasIniciales?: LineaPedido[] | undefined;
  onGuardado: (pedidoId: string) => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  /** What each product has been costing, for the margin the owner sees beside a price agreed. */
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);
  /** What the orders still to deliver set aside, when they set it aside. */
  const pendientes = useLiveQuery(() => db.pedidos.where('estado').equals('pendiente').toArray(), [], []);
  const puedeClientes = usePuede('gestionar_clientes');
  const puedeDarCredito = usePuede('dar_credito');
  const puedeElegirLista = usePuede('elegir_lista');
  const verCostos = usePuede('ver_costos');
  /** As at the till: who may type a price, and who may go below the margin floor without the owner. */
  const puedeEditarPrecio = usePuede('precio_override');
  /** The product's own price, kept: the owner's (2026-10-13). A cashier types one for this order only. */
  const puedePrecios = usePuede('editar_precios');
  const puedeCambiarPrecio = usePuede('precio_bajo_minimo');
  /** A line at another list than the client's, as at the till: the owner and a cashier. */
  const puedeOtraLista = usePuede('cobrar_otra_lista');
  const sesion = useSesion();

  const [clienteId, setClienteId] = useState(pedido?.clienteId ?? clienteInicial ?? '');
  const [fechaEntrega, setFechaEntrega] = useState(() => pedido?.fechaEntrega ?? manana());
  const [lineas, setLineas] = useState<LineaPedido[]>(lineasIniciales ?? []);
  const [nota, setNota] = useState(pedido?.nota ?? '');
  /** «Envío» as typed above the total: what the customer pays for having it brought. Empty, none. */
  const [envioTexto, setEnvioTexto] = useState(() => textoDeEnvio(pedido?.envio));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The line whose editor is open: tapped, or just added with no price on the list. */
  const [editando, setEditando] = useState<string | null>(null);
  /** The line «¿Quitar del pedido?» is asking about: from its bin, or «Quitar» in its editor. */
  const [porQuitar, setPorQuitar] = useState<string | null>(null);
  /** A price below the margin floor, from its editor, waiting on the owner's PIN as at the till. */
  const [pidiendoPin, setPidiendoPin] = useState<{ key: string; cambios: Partial<LineaEditable> } | null>(null);
  /** A product added that the list has no price for, said until it has one, as at the till. */
  const [aviso, setAviso] = useState<string | null>(null);
  /**
   * A client not on the list yet, made in a sheet over the order (the owner, 2026-10-04), with what
   * was typed in the field for their name: null while there is no sheet.
   */
  const [clienteNuevo, setClienteNuevo] = useState<string | null>(null);
  /** The last sync's word on the connection: an order that sets stock aside needs one. */
  const [fase, setFase] = useState<SyncPhase>('inactivo');
  useEffect(() => suscribirSync((s) => setFase(s.fase)), []);
  const enviado = useRef(false);

  const cliente = clientes.find((c) => c.id === clienteId);
  const activas = ordenarListas(listas);
  /**
   * The client's own list when they have one, the shop's otherwise: what the order is priced at. An
   * order being changed keeps its own while its client stays: the prices agreed were that list's.
   */
  const listaId =
    pedido && clienteId === pedido.clienteId ? pedido.listaId : (cliente?.listaId ?? listaPredeterminada(listas)?.id ?? '');
  const lista = listas.find((l) => l.id === listaId);
  const precioEnLista = (unidadVentaId: string) => precioDe(precios, unidadVentaId, listaId);

  /*
   * Another client, another list: the lines already on the order move to its prices, as the till
   * moves a sale when a client is picked. Products scanned before the client was picked, at the
   * shop's list, come out at the client's. The list the screen opened at moves nothing: an order
   * being changed opens at the prices agreed.
   */
  const listaVista = useRef(listaId);
  useEffect(() => {
    if (listaVista.current === listaId) return;
    const antes = listaVista.current;
    listaVista.current = listaId;
    // Only when the list changes, not the prices: one edited meanwhile in «Precios» moves nothing here.
    setLineas((xs) => (xs.length === 0 ? xs : conLista(xs, (u) => precioDe(precios, u, listaId), antes)));
  }, [listaId]);

  const total = totalDelPedido(lineas);
  /** What is typed in «Envío»: none while it is empty, null while it is not an amount, and the order waits. */
  const envioLeido = leerEnvio(envioTexto);
  const envio = envioLeido ?? ZERO;
  /** What the customer will pay: the lines, and the trip. Null past what the books take, as `total`. */
  const aCobrar = total === null ? null : enRango(() => addMoney(total, envio));
  const errorEnvio =
    envioLeido === null ? t('envio.mal') : total !== null && aCobrar === null && envio > 0 ? t('envio.demasiado') : null;
  const faltanPrecios = sinPrecio(lineas);
  const evento = lineasDelEvento(lineas, listaId);
  const fechaValida = /^\d{4}-\d{2}-\d{2}$/.test(fechaEntrega);
  const apartar = config.apartarPedidos;
  /** The order being changed as the books hold it now: another phone may have changed it since it opened. */
  const actual = pedido ? (pendientes.find((p) => p.id === pedido.id) ?? pedido) : undefined;
  const loQueLleva = lineas.map((l) => ({ productoId: l.producto.id, cantidad: l.cantidad, factor: l.unidad.factor }));
  /**
   * A practice shop never reaches the server, so it does not wait for its word; nor does a change
   * that asks for no more of anything than the order held, which takes nothing anybody else waits for.
   */
  const conServidor = apartar && !enPractica() && (!actual || pideMasQueAntes(loQueLleva, actual, factoresDe(unidades)));
  /** Each product the order asks more of than is free, after what the pending orders set aside. */
  const noAlcanza = apartar
    ? noAlcanzaParaElPedido(loQueLleva, { pedidos: pendientes, factorDe: factoresDe(unidades), hayDe: hayEnLaTienda(existencias), cambiado: actual })
    : [];
  /** The order being changed, as it would now say it; null for one being taken, or while it cannot be saved. */
  const cambio =
    pedido && cliente && evento
      ? { pedidoId: pedido.id, clienteId: cliente.id, fechaEntrega, listaId, ...evento, envio, nota: nota.trim() || null }
      : null;
  /** Nothing changed yet: there is nothing to save. */
  const sinCambios = cambio !== null && mismoPedido(cambio, comoEsta(pedido!));
  const valido =
    Boolean(cliente) && evento !== null && fechaValida && noAlcanza.length === 0 && envioLeido !== null && aCobrar !== null && !sinCambios;
  /** So much of a product, in the unit it is counted in: «40 libra», as «Inventario» says it. */
  const enSuUnidad = (cantidad: Milli, p: Producto) => `${formatQuantity(cantidad)} ${etiquetaUnidad(p.unidadBase, p.unidadBase)}`;

  /**
   * A product onto the order, in the presentation whose own code was scanned: the case's code, the
   * case. Something the list has no price for comes in at nothing and opens its editor, as at the
   * till, and the order is not saved until it has one.
   */
  function agregar(producto: Producto, elegida?: UnidadVenta) {
    const us = unidadesDe(unidades, producto.id);
    const unidad = us.find((u) => u.id === elegida?.id) ?? us[0];
    if (!unidad) return;
    const precio = precioEnLista(unidad.id);
    const key = newId();
    if (precio === null) {
      // Whoever may not change prices cannot type one either: the owner sets it, or the line goes.
      setAviso(t(puedeEditarPrecio ? (puedePrecios ? 'pedidos.faltaPrecio' : 'pedidos.faltaPrecioSoloAqui') : 'pedidos.faltaPrecioPida', { producto: nombre(producto) }));
      setEditando(key);
    }
    setLineas((xs) => agregarAlPedido(xs, { key, producto, unidad, precio }));
  }

  /** How many, from the line's own − and +, or its number. Never down to nothing: that is the bin's. */
  function cambiarCantidad(key: string, cambio: (actual: Milli) => Milli) {
    setLineas((xs) =>
      xs.map((l) => {
        if (l.key !== key) return l;
        const cantidad = cambio(l.cantidad);
        return cantidad > 0 ? { ...l, cantidad } : l;
      }),
    );
  }

  /** One line at another list, from the row at the foot of its card (`aLaLista`). */
  function cambiarListaDeLinea(key: string, id: string) {
    setLineas((xs) => xs.map((l) => (l.key === key ? aLaLista(l, id, precioDe(precios, l.unidad.id, id), listaId) : l)));
  }

  /**
   * A line's price lists, at the foot of its card, as at the till: when the shop has more than one,
   * and one other than the line's own has a price for what it sells. Not for whoever may not charge
   * another list without the owner: on the route a driver has nobody to ask.
   */
  function listasDeLinea(l: LineaPedido): ListasDeLinea | undefined {
    if (!puedeOtraLista || activas.length < 2) return undefined;
    const elegida = l.listaId ?? listaId;
    const opciones = activas.map((x) => ({
      id: x.id,
      nombre: etiquetaCortaLista(x),
      conPrecio: precioDe(precios, l.unidad.id, x.id) !== null,
    }));
    if (!opciones.some((o) => o.conPrecio && o.id !== elegida)) return undefined;
    return { opciones, elegida, onElegir: (id) => cambiarListaDeLinea(l.key, id) };
  }

  /** One line in another of its product's presentations, from the row at the foot of its card (`aLaPresentacion`). */
  function cambiarPresentacionDeLinea(key: string, unidadVentaId: string) {
    const unidad = unidades.find((u) => u.id === unidadVentaId);
    if (!unidad) return;
    setLineas((xs) => xs.map((l) => (l.key === key ? aLaPresentacion(l, unidad, precioDe(precios, unidad.id, l.listaId ?? listaId)) : l)));
  }

  /**
   * A line's presentations, over its lists, as at the till: when its product is sold in more than
   * one, and another than the line's has a price on the line's list. For a driver too: the box or
   * the packet is what the client asked for, not a price anybody has to allow.
   */
  function presentacionesDeLinea(l: LineaPedido): PresentacionesDeLinea | undefined {
    const us = unidadesDe(unidades, l.producto.id);
    if (us.length < 2) return undefined;
    const lista = l.listaId ?? listaId;
    const opciones = us.map((u) => ({
      id: u.id,
      nombre: etiquetaPresentacion(u, l.producto.unidadBase),
      conPrecio: precioDe(precios, u.id, lista) !== null,
    }));
    if (!opciones.some((o) => o.conPrecio && o.id !== l.unidad.id)) return undefined;
    return { opciones, elegida: l.unidad.id, onElegir: (id) => cambiarPresentacionDeLinea(l.key, id) };
  }

  /** The editor's changes, with the owner who allowed the price, when it is one that needs them. */
  function aplicar(key: string, cambios: Partial<LineaEditable>, autorizadoPor: string | null) {
    setLineas((xs) => xs.map((l) => (l.key === key ? conCambios(l, cambios, autorizadoPor) : l)));
    setEditando(null);
    setAviso(null);
  }

  async function guardar() {
    if (!valido || !cliente || !evento || enviado.current) return;
    if (pedido) return guardarCambio(pedido);
    enviado.current = true;
    setGuardando(true);
    setError(null);
    const pedidoId = newId();
    const payload = {
      pedidoId,
      clienteId: cliente.id,
      fecha: hoy(),
      fechaEntrega,
      listaId,
      ...evento,
      // Beside the lines' total, not in it: a phone that has not updated still finds that adding up.
      ...(envio > 0 ? { envio } : {}),
      ...(nota.trim() ? { nota: nota.trim() } : {}),
    };
    // Not saved: said here, the order still on screen to fix and save again.
    const noSeGuardo = (mensaje: string) => {
      setError(mensaje);
      enviado.current = false;
      setGuardando(false);
    };
    try {
      if (conServidor) {
        // First what the other phones have promised meanwhile; no connection, and nothing is recorded.
        if (!(await alDiaConElServidor())) return noSeGuardo(t('pedidos.sinConexion'));
        const antes = await probarEvento('pedido.tomado', payload);
        if (antes) return noSeGuardo(antes);
      }
      const { id, rechazo } = await emitirYVer('pedido.tomado', payload);
      // Refused (the client deactivated meanwhile, say): said here, the order still on screen.
      if (rechazo) return noSeGuardo(rechazo);
      if (conServidor) {
        const respuesta = await confirmarConElServidor(id);
        // Another phone's order got there first: the books here have already dropped this one.
        if (respuesta.estado === 'rechazado') return noSeGuardo(respuesta.motivo);
        if (respuesta.estado === 'sin-respuesta') {
          // Sent later, it must hold nothing: the cancellation goes behind it, in the same queue.
          await emitir('pedido.cancelado', { pedidoId, motivo: t('pedidos.motivoSinConfirmar') });
          return noSeGuardo(t('pedidos.noSeConfirmo'));
        }
        // In the server's log, and in its order not in the books: refused all the same.
        if ((await db.pedidos.get(pedidoId))?.estado !== 'pendiente') return noSeGuardo(t('pedidos.noQuedo'));
      }
      onGuardado(pedidoId);
    } catch (e) {
      noSeGuardo(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    }
  }

  /** The order as it now stands, said as a change of it (`pedido.actualizado`). */
  async function guardarCambio(p: Pedido) {
    if (!cambio || enviado.current) return;
    enviado.current = true;
    setGuardando(true);
    setError(null);
    // As it stood before this change: what puts it back if the server does not answer.
    const antes = comoEsta(actual ?? p);
    const noSeGuardo = (mensaje: string) => {
      setError(mensaje);
      enviado.current = false;
      setGuardando(false);
    };
    try {
      if (conServidor) {
        if (!(await alDiaConElServidor())) return noSeGuardo(t('pedidos.sinConexion'));
        const antesDeEnviar = await probarEvento('pedido.actualizado', cambio);
        if (antesDeEnviar) return noSeGuardo(antesDeEnviar);
      }
      const { id, rechazo } = await emitirYVer('pedido.actualizado', cambio);
      // Refused (delivered meanwhile on another phone, say): said here, the change still on screen.
      if (rechazo) return noSeGuardo(rechazo);
      if (conServidor) {
        const respuesta = await confirmarConElServidor(id);
        if (respuesta.estado === 'rechazado') return noSeGuardo(respuesta.motivo);
        if (respuesta.estado === 'sin-respuesta') {
          // Sent later, it must take nothing: the order as it was goes behind it, in the same queue.
          await emitir('pedido.actualizado', antes);
          return noSeGuardo(t('pedidos.cambioNoSeConfirmo'));
        }
        // In the server's log, and in its order not in the books: refused all the same.
        if (await db.sinEfecto.get(id)) return noSeGuardo(t('pedidos.cambioNoQuedo'));
      }
      onGuardado(p.id);
    } catch (e) {
      noSeGuardo(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    }
  }

  const enEdicion = lineas.find((l) => l.key === editando);
  const aQuitar = lineas.find((l) => l.key === porQuitar);
  const activos = clientes.filter((c) => c.activo).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  return (
    // Edge to edge, in bands, as the till is: who and when, the search, the lines, the total.
    <div className="-mx-5 border-t border-slate-800" data-tomar-pedido {...(pedido ? { 'data-cambiando-pedido': pedido.id } : {})}>
      <section className="grid gap-3 border-b border-slate-800 px-4 py-3 sm:grid-cols-2 sm:gap-4">
        <div>
          <div data-guia="pedido-cliente">
            <ElegirEscribiendo
              etiqueta={t('pos.cliente')}
              placeholder={t('elegir.cliente')}
              opciones={activos}
              value={clienteId}
              onChange={setClienteId}
              // The sheet for a new one, at the list's foot; the client picked stays as it was until it is saved.
              nuevo={
                puedeClientes
                  ? {
                      etiqueta: (escrito) => (escrito ? t('pos.agregarComo', { nombre: escrito }) : t('pedidos.nuevoCliente')),
                      onNuevo: setClienteNuevo,
                    }
                  : undefined
              }
              datos="cliente"
            />
          </div>
          {cliente && (
            <p className="mt-1.5 text-xs text-slate-500">
              {t('pedidos.preciosDeLista', { lista: lista ? etiquetaListaPrecio(lista) : '—' })}
            </p>
          )}
        </div>
        <div data-guia="pedido-entrega">
          <Campo
            etiqueta={t('pedidos.seEntregaEl')}
            type="date"
            value={fechaEntrega}
            min={hoy()}
            onChange={(e) => setFechaEntrega(e.target.value)}
          />
        </div>
      </section>

      <div className="border-b border-slate-800 px-4 py-3" data-guia="pedido-agregar">
        {/*
          As at the till: what a USB scanner types is read here wherever the cursor is, a code nobody
          has is asked about in a sheet, and the camera stays open for one product after another,
          each a beep and a line, with the order's total climbing beside «Listo».
        */}
        <BuscadorProducto
          onElegir={agregar}
          etiqueta={t('pedidos.agregarProducto')}
          lectorUsb
          preguntarEnHoja
          listaId={listaId}
          escaner={{
            continuo: true,
            titulo: t(pedido ? 'pedidos.editarTitulo' : 'pedidos.nuevo'),
            describir: (p, unidad) => {
              const us = unidadesDe(unidades, p.id);
              const u = us.find((x) => x.id === unidad?.id) ?? us[0];
              const linea = lineas.find((l) => l.producto.id === p.id && l.unidad.id === u?.id);
              if (!linea) return null;
              return <LineaEscaneada cantidad={linea.cantidad} precio={linea.precioUnitario} unidad={us.length > 1 ? linea.unidad : null} />;
            },
            resumen: lineas.length > 0 && aCobrar !== null && (
              <div>
                <p className="text-xs text-slate-400">{t('pedidos.enElPedido', { n: lineas.length })}</p>
                <p key={aCobrar} className="tabular late text-2xl font-bold tracking-tight">
                  {formatMoney(aCobrar)}
                </p>
              </div>
            ),
          }}
        />
      </div>

      {/* It is about an unpriced line, so it goes once none is left: priced, or taken off. */}
      {aviso && faltanPrecios > 0 && (
        <p className="border-b border-amber-900 bg-amber-950/60 px-4 py-2 text-xs text-amber-200">{aviso}</p>
      )}

      <div className="px-4 py-3">
        {lineas.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <span className="chip chip-neutro size-16 rounded-2xl">
              <Ico icono="pedidos" className="size-8" />
            </span>
            <p className="text-sm text-slate-500">{t('pedidos.busqueEmpezar')}</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {lineas.map((l) => {
              const nombreLinea = nombre(l.producto);
              const corto = noAlcanza.find((f) => f.productoId === l.producto.id);
              return (
                <LineaDeCarrito
                  key={l.key}
                  data-guia="pedido-linea"
                  producto={l.producto}
                  nombre={nombreLinea}
                  cantidad={l.cantidad}
                  onCambiarCantidad={(cambio) => cambiarCantidad(l.key, cambio)}
                  subtotal={subtotalDe(l) ?? (0 as Centavos)}
                  precio={l.precioUnitario}
                  precioLista={l.precioLista}
                  unidad={l.unidad}
                  aviso={corto && t('pedidos.lineaNoAlcanza', { libre: enSuUnidad(corto.libre, l.producto) })}
                  presentaciones={presentacionesDeLinea(l)}
                  listas={listasDeLinea(l)}
                  etiquetaQuitar={t('pedidos.quitarLinea', { producto: nombreLinea })}
                  onAbrir={() => setEditando(l.key)}
                  onQuitar={() => setPorQuitar(l.key)}
                />
              );
            })}
          </ul>
        )}
      </div>

      {lineas.length > 0 && (
        <div className="flex flex-col gap-3 border-t border-slate-800 px-4 py-3">
          <Campo etiqueta={t('comun.nota')} value={nota} onChange={(e) => setNota(e.target.value)} autoComplete="off" maxLength={300} />
          <p className="text-xs text-slate-500" data-pedido-explicacion={apartar ? 'aparta' : 'promesa'}>
            {t(apartar ? 'pedidos.explicacionApartado' : 'pedidos.explicacion')}
          </p>
        </div>
      )}

      {/* The till's foot: the total, what still stops it, and the button. In sight while the lines scroll. */}
      <footer className="sticky bottom-0 z-10 border-t border-slate-800 bg-slate-950 px-4 py-4" data-pedido-pie>
        {error && <p className="mb-3 text-sm text-rose-400">{error}</p>}
        <CampoEnvio valor={envioTexto} onCambio={setEnvioTexto} error={errorEnvio} />
        <div className="mb-3 flex items-baseline justify-between gap-3" data-pedido-total>
          <span className="text-sm text-slate-400">{t('comun.total')}</span>
          <span key={aCobrar ?? '—'} className="tabular late text-4xl font-bold tracking-tight max-[359px]:text-3xl">
            {aCobrar === null ? '—' : formatMoney(aCobrar)}
          </span>
        </div>
        {faltanPrecios > 0 && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            {t(puedeEditarPrecio ? 'pedidos.hayLineasSinPrecio' : 'pedidos.hayLineasSinPrecioPida', { n: faltanPrecios })}
          </p>
        )}
        {lineas.length > 0 && !cliente && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200">{t('pedidos.faltaCliente')}</p>
        )}
        {noAlcanza.length > 0 && (
          <div className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-200" data-pedido-no-alcanza>
            {noAlcanza.map((f) => {
              const producto = lineas.find((l) => l.producto.id === f.productoId)?.producto;
              if (!producto) return null;
              return (
                <p key={f.productoId} data-no-alcanza={f.productoId}>
                  {t('pedidos.noAlcanza', { producto: nombre(producto), libre: enSuUnidad(f.libre, producto), pide: enSuUnidad(f.pide, producto) })}
                </p>
              );
            })}
            <p className="mt-1 text-xs text-rose-300/80">{t('pedidos.noAlcanzaQue')}</p>
          </div>
        )}
        {conServidor && lineas.length > 0 && fase === 'sin-conexion' && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-pedido-sin-conexion>
            {t('pedidos.sinConexion')}
          </p>
        )}
        <button
          type="button"
          className="btn-primario h-16 w-full text-lg"
          data-guia="pedido-guardar"
          disabled={!valido || guardando}
          onClick={() => void guardar()}
          {...(pedido ? { 'data-sin-cambios': sinCambios ? 'si' : 'no' } : {})}
        >
          <Ico icono="guardar" className="size-6" />
          {t(guardando ? (conServidor ? 'pedidos.confirmando' : 'comun.guardando') : pedido ? 'pedidos.guardarCambios' : 'pedidos.guardar')}
        </button>
      </footer>

      {enEdicion && (
        <EditorLinea
          linea={{
            descripcion: enEdicion.producto.nombre,
            unidad: enEdicion.unidad,
            cantidad: enEdicion.cantidad,
            precioLista: enEdicion.precioLista,
            precioUnitario: enEdicion.precioUnitario,
            motivoOverride: enEdicion.motivoOverride,
            costoReferencia: costoConocido(enEdicion.producto.id, 'tienda', existencias, compras),
            productoId: enEdicion.producto.id,
          }}
          titulo={nombre(enEdicion.producto)}
          unidades={unidadesDe(unidades, enEdicion.producto.id)}
          puedeEditarPrecio={puedeEditarPrecio}
          puedeCambiarPrecio={puedeCambiarPrecio}
          verCostos={verCostos}
          margenMinimoBp={config.precioMargenMinimoBp}
          // Its presentations at the line's own list, as at the till.
          precioDe={(u) => precioDe(precios, u, enEdicion.listaId ?? listaId)}
          onCerrar={() => setEditando(null)}
          onQuitar={() => {
            // The question replaces the sheet rather than stacking on it, as at the till.
            setEditando(null);
            setPorQuitar(enEdicion.key);
          }}
          onGuardar={(cambios, requiereAutorizacion, delDueno) => {
            if (requiereAutorizacion) setPidiendoPin({ key: enEdicion.key, cambios });
            // The owner's own price below the floor names them: somebody else may ring the sale.
            else aplicar(enEdicion.key, cambios, delDueno ? (sesion?.usuarioId ?? null) : null);
          }}
        />
      )}

      {pidiendoPin && (
        <PedirPin
          motivo={t('pos.motivoAutorizacion')}
          onCancelar={() => setPidiendoPin(null)}
          onAutorizado={(usuarioId) => {
            aplicar(pidiendoPin.key, pidiendoPin.cambios, usuarioId);
            setPidiendoPin(null);
          }}
        />
      )}

      {aQuitar && (
        <Confirmar
          titulo={t('preguntar.quitarDelPedido')}
          mensaje={t('pedidos.confirmarQuitar', {
            producto: nombre(aQuitar.producto),
            total: aQuitar.precioUnitario > 0 ? formatMoney(subtotalDe(aQuitar) ?? (0 as Centavos)) : t('pos.sinPrecio'),
          })}
          confirmar={t('comun.quitar')}
          icono="quitar"
          peligro
          onCerrar={() => setPorQuitar(null)}
          onConfirmar={() => setLineas((xs) => xs.filter((x) => x.key !== aQuitar.key))}
        />
      )}

      {/* Made here, the order as it was behind the sheet, and once saved it is the client picked. */}
      {clienteNuevo !== null && (
        <Modal titulo={t('pos.clienteNuevo')} onCerrar={() => setClienteNuevo(null)}>
          <ClienteNuevoEnPedido
            nombreInicial={clienteNuevo}
            clientes={clientes}
            listas={activas}
            puedeDarCredito={puedeDarCredito}
            puedeElegirLista={puedeElegirLista}
            onListo={(id) => {
              setClienteId(id);
              setClienteNuevo(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

/**
 * A new client from the order's list, in a sheet: the form «Clientes» adds one with, every field of
 * it, since an order's client is often a shop on the route with its own list and visiting day. A
 * name an active client already goes by is said, with that one to pick instead.
 */
function ClienteNuevoEnPedido({
  nombreInicial,
  clientes,
  listas,
  puedeDarCredito,
  puedeElegirLista,
  onListo,
}: {
  /** What was typed in the order's field, and not found: their name, likely. */
  nombreInicial: string;
  clientes: readonly Cliente[];
  listas: readonly ListaPrecio[];
  puedeDarCredito: boolean;
  puedeElegirLista: boolean;
  /** The client saved, or the one already called that: the one picked for the order. */
  onListo: (clienteId: string) => void;
}) {
  const t = useT();
  const [b, setB] = useState<BorradorCliente>(() => ({ ...CLIENTE_VACIO, nombre: nombreInicial }));
  const [error, setError] = useState<string | null>(null);
  const enviado = useRef(false);
  const yaEsta = clienteLlamado(clientes, b.nombre);

  async function guardar() {
    if (!b.nombre.trim() || yaEsta || enviado.current) return;
    enviado.current = true;
    setError(null);
    const clienteId = newId();
    try {
      const { rechazo } = await emitirYVer('cliente.creado', {
        clienteId,
        ...camposDe(b, { limite: puedeDarCredito, lista: puedeElegirLista, cedula: true }),
      });
      if (rechazo) {
        setError(rechazo);
        enviado.current = false;
        return;
      }
      // What the notebook says they already owed, when they are new to the app but not to the shop.
      const anterior = saldoAnteriorDe(b);
      if (puedeDarCredito && anterior) await emitir('cliente.saldo_inicial', { clienteId, monto: anterior });
      onListo(clienteId);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
      enviado.current = false;
    }
  }

  return (
    <div data-cliente-nuevo-pedido>
      <p className="mb-4 text-sm text-slate-400">{t('pedidos.clienteNuevoAyuda')}</p>
      <FormularioCliente
        b={b}
        setB={setB}
        listas={listas}
        puedeDarCredito={puedeDarCredito}
        puedeElegirLista={puedeElegirLista}
        conCedula
        enHoja
        yaEsta={yaEsta}
        onElegir={onListo}
        onGuardar={() => void guardar()}
      />
      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
    </div>
  );
}
